/**
 * scroll-reveal.js
 *
 * Progressive-enhancement behaviors that depend on the IntersectionObserver API:
 *   1. Reveal `.reveal` elements as they scroll into the viewport.
 *   2. Lazy-load below-the-fold background images declared via `data-bg`.
 *   3. Make clickable card containers keyboard-accessible.
 *
 * Vanilla ES2017+, no dependencies. Safe to load with `defer`.
 */

/**
 * Animate `.reveal` elements into view as they enter the viewport.
 * Each element is unobserved after its first reveal so the observer
 * frees the reference and never re-triggers.
 */
(function initScrollReveal() {
  'use strict';

  const els = document.querySelectorAll('.reveal');
  if (!els.length) return;

  // Graceful degradation: if IntersectionObserver is unavailable, reveal
  // everything immediately so content is never trapped behind the animation.
  if (!('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  els.forEach((el) => observer.observe(el));
})();

/**
 * Lazy-load below-the-fold background images declared via a `data-bg`
 * attribute. The image URL is only assigned once the element nears the
 * viewport (200px lookahead), reducing initial page weight.
 */
(function initLazyBg() {
  'use strict';

  const lazyEls = document.querySelectorAll('[data-bg]');
  if (!lazyEls.length) return;

  // Fallback: assign all background images up front when the observer
  // API is missing so no element is left without its background.
  if (!('IntersectionObserver' in window)) {
    lazyEls.forEach((el) => {
      if (el.dataset.bg) el.style.backgroundImage = 'url("' + el.dataset.bg + '")';
    });
    return;
  }

  const lazyObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      if (el.dataset.bg) {
        // Quote the URL so values containing parentheses or spaces cannot
        // break out of the CSS `url()` function.
        el.style.backgroundImage = 'url("' + el.dataset.bg + '")';
      }
      lazyObserver.unobserve(el);
    });
  }, { rootMargin: '200px' });

  lazyEls.forEach((el) => lazyObserver.observe(el));
})();

/**
 * Make clickable card containers (`.product-card`, `.collection-card`)
 * keyboard-accessible by exposing them as links and forwarding
 * Enter / Space activation to the card's primary anchor.
 */
(function initCardKeyboard() {
  'use strict';

  document.querySelectorAll('.product-card, .collection-card').forEach((card) => {
    const link = card.querySelector('a[href]');
    if (!link) return;

    card.setAttribute('tabindex', '0');
    card.setAttribute('role', 'link');

    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        link.click();
      }
    });
  });
})();
